import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { CreditCard, Wallet, Landmark, ArrowRight, Award } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "@/components/ImageUploader";

const PaymentOption = ({ id, value, current, onChange, icon, title, desc }) => (
  <label
    htmlFor={id}
    className={`flex gap-4 items-start p-5 cursor-pointer transition-all border ${current === value ? "border-[#D4AF37] bg-[#FAF7EE]" : "border-[#EBE5DF] hover:border-[#2C1E16]"}`}
    data-testid={`payment-${value}`}
  >
    <input type="radio" id={id} name="payment" value={value} checked={current === value} onChange={() => onChange(value)} className="mt-1" />
    <div className="flex-1">
      <div className="flex items-center gap-3 mb-1">
        {icon}
        <span className="font-serif text-lg">{title}</span>
      </div>
      <p className="text-xs text-[#736B66]">{desc}</p>
    </div>
  </label>
);

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState(null); // {code, discount, free_shipping}
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [loyalty, setLoyalty] = useState(null); // {points, max_redeemable_points, max_redeemable_value, redeem_rate}
  const [redeemInput, setRedeemInput] = useState(0);
  const [redeemApplied, setRedeemApplied] = useState(0);
  const baseShipping = subtotal >= 50 || subtotal === 0 ? 0 : 4.9;
  const shipping = coupon?.free_shipping ? 0 : baseShipping;
  const discount = coupon?.discount || 0;
  const loyaltyDiscount = redeemApplied > 0 && loyalty ? redeemApplied / loyalty.redeem_rate : 0;
  const total = Math.max(0, subtotal + shipping - discount - loyaltyDiscount);

  useEffect(() => {
    if (user) {
      api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => {});
      // Sync form fields when user loads asynchronously
      setForm((f) => ({
        ...f,
        full_name: f.full_name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    street: "",
    city: "",
    postal_code: "",
    country: "GR",
    notes: "",
  });
  const [payment, setPayment] = useState("card");
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    try {
      const res = await api.post("/coupons/validate", { code: couponInput.trim().toUpperCase(), subtotal });
      setCoupon(res.data);
      toast.success(t("checkout.couponApplied") + ": " + res.data.code);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.couponInvalid"));
      setCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
  };

  const applyRedeem = () => {
    if (!loyalty) return;
    let pts = Math.max(0, parseInt(redeemInput || 0));
    if (pts < 100) {
      toast.error(t("loyalty.minRedeem"));
      return;
    }
    if (pts > loyalty.max_redeemable_points) {
      pts = loyalty.max_redeemable_points;
    }
    // round down to multiple of redeem_rate
    pts = Math.floor(pts / loyalty.redeem_rate) * loyalty.redeem_rate;
    setRedeemApplied(pts);
    toast.success(`${pts} ${t("loyalty.points")} → €${(pts / loyalty.redeem_rate).toFixed(2)}`);
  };

  const removeRedeem = () => {
    setRedeemApplied(0);
    setRedeemInput(0);
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-24 text-center">
        <p className="text-[#736B66] mb-6">{t("cart.empty")}</p>
        <Link to="/shop" className="luxury-btn">{t("cart.emptyCta")}</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.street || !form.city || !form.postal_code) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!user && !form.email) {
      toast.error("Email required for guest checkout");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_id: i.variant_id || null })),
        shipping_address: {
          full_name: form.full_name,
          phone: form.phone,
          street: form.street,
          city: form.city,
          postal_code: form.postal_code,
          country: form.country,
          notes: form.notes || null,
        },
        payment_method: payment,
        origin_url: window.location.origin,
        email: form.email || null,
        notes: form.notes || null,
        coupon_code: coupon?.code || null,
        loyalty_points_redeem: redeemApplied || 0,
      };
      const res = await api.post("/orders", payload);
      const data = res.data;

      if ((payment === "card" || payment === "stripe") && data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
        return;
      }

      // Non-stripe: clear cart & navigate to success page with order id
      clear();
      navigate(`/checkout/success?order_id=${data.order_id}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Order failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-[1300px] mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-serif text-4xl lg:text-5xl mb-10">{t("checkout.title")}</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_420px] gap-12">
        <div className="space-y-10">
          {/* Contact */}
          <section>
            <h2 className="font-serif text-2xl mb-6 pb-3 border-b border-[#EBE5DF]">{t("checkout.contact")}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="overline mb-1 block">{t("checkout.fullName")} *</label>
                <input className="luxury-input-box" required value={form.full_name} onChange={onChange("full_name")} data-testid="input-fullname" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("checkout.email")} *</label>
                <input type="email" className="luxury-input-box" required value={form.email} onChange={onChange("email")} data-testid="input-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("checkout.phone")} *</label>
                <input className="luxury-input-box" required value={form.phone} onChange={onChange("phone")} data-testid="input-phone" />
              </div>
            </div>
          </section>

          {/* Shipping */}
          <section>
            <h2 className="font-serif text-2xl mb-6 pb-3 border-b border-[#EBE5DF]">{t("checkout.shipping")}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("checkout.street")} *</label>
                <input className="luxury-input-box" required value={form.street} onChange={onChange("street")} data-testid="input-street" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("checkout.city")} *</label>
                <input className="luxury-input-box" required value={form.city} onChange={onChange("city")} data-testid="input-city" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("checkout.postalCode")} *</label>
                <input className="luxury-input-box" required value={form.postal_code} onChange={onChange("postal_code")} data-testid="input-postal" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("checkout.country")}</label>
                <select className="luxury-input-box" value={form.country} onChange={onChange("country")} data-testid="select-country">
                  <option value="GR">Ελλάδα / Greece</option>
                  <option value="CY">Κύπρος / Cyprus</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("checkout.notes")}</label>
                <textarea className="luxury-input-box" rows={3} value={form.notes} onChange={onChange("notes")} data-testid="input-notes" />
              </div>
            </div>
          </section>

          {/* Payment */}
          <section>
            <h2 className="font-serif text-2xl mb-6 pb-3 border-b border-[#EBE5DF]">{t("checkout.payment")}</h2>
            <div className="space-y-3">
              <PaymentOption id="pay-card" value="card" current={payment} onChange={setPayment}
                icon={<CreditCard size={20} className="text-[#D4AF37]" />}
                title={t("checkout.payCard")} desc={t("checkout.payCardDesc")} />
              <PaymentOption id="pay-paypal" value="paypal" current={payment} onChange={setPayment}
                icon={<Wallet size={20} className="text-[#D4AF37]" />}
                title={t("checkout.payPaypal")} desc={t("checkout.payPaypalDesc")} />
              <PaymentOption id="pay-bank" value="bank_transfer" current={payment} onChange={setPayment}
                icon={<Landmark size={20} className="text-[#D4AF37]" />}
                title={t("checkout.payBank")} desc={t("checkout.payBankDesc")} />
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="bg-[#F5F2ED] p-8 h-fit lg:sticky lg:top-28" data-testid="checkout-summary">
          <h3 className="font-serif text-2xl mb-6">{t("checkout.orderSummary")}</h3>
          <div className="space-y-3 mb-6 max-h-72 overflow-y-auto">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-3 text-sm">
                <div className="w-14 h-14 bg-white overflow-hidden">
                  {it.image && <img src={resolveImageUrl(it.image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium leading-tight line-clamp-2">{it.name_el}</p>
                  <p className="text-[#736B66]">×{it.quantity}</p>
                </div>
                <p className="font-semibold">{(it.price * it.quantity).toFixed(2)}€</p>
              </div>
            ))}
          </div>
          <div className="border-t border-[#EBE5DF] pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>{t("cart.subtotal")}</span><span>{subtotal.toFixed(2)}€</span></div>
            <div className="flex justify-between"><span>{t("cart.shipping")}</span><span>{shipping === 0 ? t("cart.free") : `${shipping.toFixed(2)}€`}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-[#8B0000]" data-testid="checkout-discount-row">
                <span>{t("checkout.discount")} ({coupon?.code})</span>
                <span>-{discount.toFixed(2)}€</span>
              </div>
            )}
            {loyaltyDiscount > 0 && (
              <div className="flex justify-between text-[#D4AF37]" data-testid="checkout-loyalty-row">
                <span>{t("loyalty.redeemed_label")} ({redeemApplied} pts)</span>
                <span>-{loyaltyDiscount.toFixed(2)}€</span>
              </div>
            )}
          </div>

          {/* Coupon */}
          <div className="mt-4 pt-4 border-t border-[#EBE5DF]" data-testid="coupon-section">
            {coupon ? (
              <div className="flex items-center justify-between gap-3 bg-white p-3 border border-[#D4AF37]">
                <div className="text-xs">
                  <p className="font-semibold text-[#2C1E16]">{coupon.code}</p>
                  <p className="text-[#736B66]">{coupon.description || t("checkout.couponApplied")}</p>
                </div>
                <button type="button" onClick={removeCoupon} className="text-xs text-[#736B66] hover:text-[#8B0000] underline" data-testid="remove-coupon">
                  {t("checkout.removeCoupon")}
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={t("checkout.couponPlaceholder")}
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  className="luxury-input-box flex-1 text-sm uppercase tracking-wider"
                  data-testid="coupon-input"
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={couponLoading || !couponInput.trim()}
                  className="luxury-btn-outline text-xs px-4"
                  data-testid="apply-coupon"
                >
                  {couponLoading ? "..." : t("checkout.applyCoupon")}
                </button>
              </div>
            )}
          </div>
          {/* Loyalty redeem (only logged-in users with enough points) */}
          {user && loyalty && loyalty.max_redeemable_points >= 100 && (
            <div className="mt-3 pt-3 border-t border-[#EBE5DF]" data-testid="loyalty-section">
              {redeemApplied > 0 ? (
                <div className="flex items-center justify-between gap-3 bg-white p-3 border border-[#D4AF37]">
                  <div className="text-xs">
                    <p className="font-semibold text-[#2C1E16] flex items-center gap-1.5">
                      <Award size={14} className="text-[#D4AF37]" />
                      {redeemApplied} {t("loyalty.points")} → €{(redeemApplied / loyalty.redeem_rate).toFixed(2)}
                    </p>
                    <p className="text-[#736B66]">{t("loyalty.redeemed_label")}</p>
                  </div>
                  <button type="button" onClick={removeRedeem} className="text-xs text-[#736B66] hover:text-[#8B0000] underline" data-testid="remove-loyalty">
                    {t("loyalty.removeRedeem")}
                  </button>
                </div>
              ) : (
                <div>
                  <p className="overline mb-2 text-[#D4AF37] flex items-center gap-1.5">
                    <Award size={12} /> {t("loyalty.redeemAtCheckout")}
                  </p>
                  <p className="text-xs text-[#736B66] mb-2">
                    {t("loyalty.availablePoints")}: <span className="font-semibold text-[#2C1E16]">{loyalty.points}</span> (έως €{loyalty.max_redeemable_value.toFixed(2)})
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="100"
                      step="20"
                      max={loyalty.max_redeemable_points}
                      value={redeemInput || ""}
                      onChange={(e) => setRedeemInput(e.target.value)}
                      placeholder={t("loyalty.redeemAmount")}
                      className="luxury-input-box flex-1 text-sm"
                      data-testid="loyalty-input"
                    />
                    <button
                      type="button"
                      onClick={applyRedeem}
                      className="luxury-btn-outline text-xs px-4"
                      data-testid="apply-loyalty"
                    >
                      {t("loyalty.applyRedeem")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between text-lg font-semibold pt-3 mt-3 border-t border-[#EBE5DF]" data-testid="checkout-total">
            <span>{t("cart.total")}</span>
            <span>{total.toFixed(2)}€</span>
          </div>
          <button type="submit" disabled={submitting} className="luxury-btn w-full mt-6" data-testid="place-order-button">
            {submitting ? "..." : t("checkout.placeOrder")} <ArrowRight size={16} className="hero-arrow" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
